// Shared staff sign-in (design_handoff_audit_redesigns, Area 6). Every staff tool passes
// in its own name and gets the same form, the same inline error states, password reset
// and the same "access needed" screen, instead of nine copies with their own CSS prefix.
//
//   gateTool({ toolName, requiredRole, reason, parent, onReady })
//     Standalone tools: owns onAuthStateChanged, checks /users/{uid}/role, and calls
//     onReady(user, role) once with a signed-in user who may use the tool.
//
//   createSignIn({ toolName, host, footerHtml, sharedTablet })
//     Pages with their own auth flow (index, techs): just the screen — show(), hide(),
//     showAccess().
//
// Shared tablets (Area 6C, gateTool's sharedTablet: true — the launch tablets): an admin
// signs in once and pairs the tablet; after that the sign-in is a crew picker and a
// 4-digit PIN, checked by the pinSignIn Cloud Function (functions/sharedTablet.js), which
// hands back a sign-in token for that person. The tablet only keeps its device id and a
// random secret — never a PIN. It locks itself after 20 minutes without a touch.

import { auth, database, functions } from './initFirebase';
import {
  onAuthStateChanged, signInWithEmailAndPassword, signInWithPopup, GoogleAuthProvider,
  sendPasswordResetEmail, signOut, signInWithCustomToken,
} from 'firebase/auth';
import { ref, get } from 'firebase/database';
import { httpsCallable } from 'firebase/functions';
import { getUserRole, isAdminRole, escapeHTML } from './utilities';
import { brandLogo, cachedBrand, rememberBrand } from './brandCache';

const DEFAULT_LOGO = '/images/logo/logo-192.webp';
const DEVICE_KEY = 'sharedDevice';
const PAIR_PENDING_KEY = 'si-pair-pending';
const IDLE_LOCK_MS = 20 * 60 * 1000;

/** This tablet's pairing ({ deviceId, secret, name }), or null. */
export function pairedDevice() {
  try {
    const d = JSON.parse(localStorage.getItem(DEVICE_KEY) || 'null');
    return d && d.deviceId && d.secret ? d : null;
  } catch { return null; }
}
function forgetDevice() { try { localStorage.removeItem(DEVICE_KEY); } catch { /* ignore */ } }
const pairPending = () => { try { return sessionStorage.getItem(PAIR_PENDING_KEY) === '1'; } catch { return false; } };
const setPairPending = on => { try { if (on) sessionStorage.setItem(PAIR_PENDING_KEY, '1'); else sessionStorage.removeItem(PAIR_PENDING_KEY); } catch { /* ignore */ } };
const fmtDay = ms => (ms ? new Date(ms).toLocaleDateString(undefined, { month: 'short', day: 'numeric' }) : '');
const pairedLine = d => (d && d.pairedBy ? `Paired by ${d.pairedBy}${d.pairedAt ? ` · ${fmtDay(d.pairedAt)}` : ''}` : '');
const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

function roleLabel(role) {
  const parts = String(role || '').split(',').map(s => s.trim()).filter(Boolean);
  return parts.map(p => p.charAt(0).toUpperCase() + p.slice(1)).join(' · ');
}

function initialsOf(name) {
  return String(name || '').split(/[\s@.]+/).filter(Boolean).slice(0, 2)
    .map(w => w.charAt(0).toUpperCase()).join('') || '?';
}

let brandRequested = false;
function loadBrand() {
  if (brandRequested) return;
  brandRequested = true;
  // settings/businessProfile is world-readable so the brand paints before sign-in.
  get(ref(database, 'settings/businessProfile'))
    .then(snap => { if (snap.exists()) rememberBrand(snap.val()); })
    .catch(() => { /* offline: the cached brand stays */ });
}

export function createSignIn({ toolName, host = null, footerHtml = '', sharedTablet = false } = {}) {
  const root = host || document.createElement('div');
  root.classList.add('si-root');
  root.style.display = 'none';
  if (!host) document.body.appendChild(root);

  const brand = cachedBrand() || {};
  root.innerHTML = `
    <div class="si-frame" role="dialog" aria-labelledby="siTitle">
      <aside class="si-brand">
        <div class="si-logo-tile">
          <img data-brand="logo" src="${escapeHTML(brandLogo(brand.logoUrl) || DEFAULT_LOGO)}" alt="" width="46" height="46">
        </div>
        <div class="si-biz" data-brand="name">${escapeHTML(brand.name || '')}</div>
        <div class="si-brand-spacer"></div>
        <div class="si-tool">
          <div class="si-tool-kicker">Signing in to</div>
          <div class="si-tool-name">${escapeHTML(toolName)}</div>
          <div class="si-tool-sub">Staff accounts</div>
        </div>
      </aside>
      <main class="si-main">
        <form class="si-pane" data-pane="form" novalidate>
          <h1 class="si-title" id="siTitle">Sign in</h1>
          <div class="si-panel si-panel-neutral si-notice" hidden></div>
          <div class="si-field" data-field="email">
            <label for="siEmail">Email</label>
            <input id="siEmail" type="email" autocomplete="username" inputmode="email" spellcheck="false" autocapitalize="off">
            <div class="si-error" aria-live="polite"></div>
          </div>
          <div class="si-field" data-field="password">
            <div class="si-label-row">
              <label for="siPassword">Password</label>
              <button type="button" class="si-link" data-act="forgot">Forgot password?</button>
            </div>
            <div class="si-input-wrap">
              <input id="siPassword" type="password" autocomplete="current-password">
              <button type="button" class="si-show" data-act="show" aria-pressed="false">Show</button>
            </div>
            <div class="si-error" aria-live="polite"></div>
            <div class="si-caps" hidden>Caps Lock is on</div>
          </div>
          <button type="submit" class="si-btn si-btn-primary" data-act="submit">Sign in</button>
          <div class="si-or"><span></span>or<span></span></div>
          <button type="button" class="si-btn si-btn-outline" data-act="google">
            <img src="/assets/google_logo.png" width="20" height="20" alt="">Continue with Google
          </button>
          ${footerHtml ? `<div class="si-footer">${footerHtml}</div>` : ''}
          ${sharedTablet ? `<div class="si-footer si-tablet-footer">
            <span data-tablet="unpaired">Using a shared launch tablet? <button type="button" class="si-link" data-act="pair">Pair this device</button></span>
            <span data-tablet="paired" hidden><button type="button" class="si-link" data-act="crew">‹ Back to the crew list</button></span>
          </div>` : ''}
        </form>

        <form class="si-pane" data-pane="pair" novalidate hidden>
          <h1 class="si-title">Pair this tablet</h1>
          <p class="si-text si-pair-who"></p>
          <div class="si-field" data-field="deviceName">
            <label for="siDeviceName">Tablet name</label>
            <input id="siDeviceName" type="text" autocomplete="off" placeholder="e.g. Launch 2 tablet" maxlength="60">
            <div class="si-error" aria-live="polite"></div>
          </div>
          <p class="si-text si-small">Once it's paired, crew sign in here by picking their name and entering their PIN. Each person sets their PIN in the Techs app (Me) or Settings › My profile. Unpair it any time in Settings › My profile.</p>
          <button type="submit" class="si-btn si-btn-primary" data-act="doPair">Pair this tablet</button>
          <button type="button" class="si-link si-back" data-act="cancelPair">Cancel</button>
        </form>

        <form class="si-pane" data-pane="reset" novalidate hidden>
          <h1 class="si-title">Reset password</h1>
          <p class="si-text">Enter your staff email and we'll send you a link to choose a new password.</p>
          <div class="si-field" data-field="resetEmail">
            <label for="siResetEmail">Email</label>
            <input id="siResetEmail" type="email" autocomplete="username" inputmode="email" spellcheck="false" autocapitalize="off">
            <div class="si-error" aria-live="polite"></div>
          </div>
          <button type="submit" class="si-btn si-btn-primary" data-act="sendReset">Send reset link</button>
          <button type="button" class="si-link si-back" data-act="back">Back to sign in</button>
        </form>

        <div class="si-pane" data-pane="message" hidden>
          <div class="si-panel si-msg">
            <div class="si-panel-title"></div>
            <div class="si-panel-text"></div>
          </div>
          <button type="button" class="si-btn si-btn-navy" data-act="msgAction"></button>
          <button type="button" class="si-link si-back" data-act="back">Back to sign in</button>
        </div>

        <div class="si-pane si-access" data-pane="access" hidden></div>
      </main>
    </div>
    ${sharedTablet ? `<div class="si-pin-frame" hidden role="dialog" aria-labelledby="siPinWho">
      <section class="si-crew-side">
        <div class="si-device">
          <div class="si-logo-tile si-logo-sm"><img data-brand="logo" src="${escapeHTML(brandLogo(brand.logoUrl) || DEFAULT_LOGO)}" alt="" width="32" height="32"></div>
          <div><div class="si-device-name"></div><div class="si-device-sub"></div></div>
        </div>
        <h1 class="si-crew-title">Who's driving?</h1>
        <div class="si-crew-grid" role="listbox" aria-label="Crew"></div>
        <div class="si-crew-foot">Locks after 20 minutes idle. Not on this list? <button type="button" class="si-link si-link-mint" data-act="email">Sign in with email</button></div>
      </section>
      <section class="si-pin-side">
        <div class="si-pin-who" id="siPinWho">Pick your name</div>
        <div class="si-pin-dots" aria-hidden="true"><span></span><span></span><span></span><span></span></div>
        <div class="si-pin-msg" aria-live="polite"></div>
        <div class="si-keypad">
          ${['1', '2', '3', '4', '5', '6', '7', '8', '9', '', '0', '⌫'].map(k => (k
            ? `<button type="button" class="si-key" data-key="${k === '⌫' ? 'back' : k}" aria-label="${k === '⌫' ? 'Delete' : k}">${k}</button>`
            : '<span></span>')).join('')}
        </div>
      </section>
    </div>` : ''}`;

  const $ = sel => root.querySelector(sel);
  const emailInput = $('#siEmail');
  const passInput = $('#siPassword');
  const resetInput = $('#siResetEmail');
  const capsEl = $('.si-caps');
  const pinFrame = $('.si-pin-frame');
  let busy = false;
  let msgAction = null;

  function showPane(name) {
    root.querySelectorAll('.si-pane').forEach(p => { p.hidden = p.dataset.pane !== name; });
    root.querySelector('.si-frame').classList.toggle('si-frame-access', name === 'access');
    root.querySelector('.si-frame').hidden = false;
    if (pinFrame) pinFrame.hidden = true;
    if (sharedTablet) {
      const paired = !!pairedDevice();
      root.querySelector('[data-tablet="unpaired"]').hidden = paired;
      root.querySelector('[data-tablet="paired"]').hidden = !paired;
    }
  }

  function setFieldError(field, text) {
    const el = root.querySelector(`[data-field="${field}"]`);
    el.classList.toggle('si-invalid', !!text);
    el.querySelector('.si-error').textContent = text || '';
  }

  function clearErrors() {
    ['email', 'password', 'resetEmail', ...(sharedTablet ? ['deviceName'] : [])].forEach(f => setFieldError(f, ''));
  }

  function setBusy(on, label) {
    busy = on;
    root.querySelectorAll('.si-btn').forEach(b => { b.disabled = on; });
    const submit = $('[data-act="submit"]');
    submit.textContent = on && label ? label : 'Sign in';
  }

  function showMessage({ tone, title, text, actionLabel, action }) {
    const panel = $('.si-msg');
    panel.className = `si-panel si-msg si-panel-${tone}`;
    panel.querySelector('.si-panel-title').textContent = title;
    panel.querySelector('.si-panel-text').textContent = text;
    const btn = $('[data-act="msgAction"]');
    btn.hidden = !actionLabel;
    btn.textContent = actionLabel || '';
    msgAction = action || null;
    showPane('message');
  }

  function showOffline() {
    showMessage({
      tone: 'neutral',
      title: "You're offline",
      text: 'Sign-in needs a connection. If you signed in on this device before, the Techs app still opens with saved work.',
      actionLabel: 'Try again',
      action: () => { if (navigator.onLine) showPane('form'); },
    });
  }

  function showLocked() {
    showMessage({
      tone: 'amber',
      title: 'Too many attempts',
      text: 'Sign-in is paused for this account for a few minutes. Wait and try again, or reset your password.',
      actionLabel: 'Reset password',
      action: () => openReset(),
    });
  }

  function openReset() {
    clearErrors();
    resetInput.value = emailInput.value.trim();
    showPane('reset');
    resetInput.focus();
  }

  // Firebase projects with email-enumeration protection (the default since 2023) answer
  // auth/invalid-credential for both an unknown email and a wrong password, so that one
  // can't name the field; the older, specific codes still get their own message.
  function handleAuthError(err) {
    const code = err && err.code;
    if (code === 'auth/network-request-failed' || !navigator.onLine) return showOffline();
    if (code === 'auth/too-many-requests') return showLocked();
    if (code === 'auth/invalid-email') return setFieldError('email', "That isn't a valid email address.");
    if (code === 'auth/user-not-found') {
      return setFieldError('email', 'No staff account uses this email. Check the spelling, or ask an admin to add you.');
    }
    if (code === 'auth/wrong-password') return setFieldError('password', "That password doesn't match this account.");
    if (code === 'auth/user-disabled') return setFieldError('email', 'This account is turned off. Ask an admin.');
    if (code === 'auth/invalid-credential' || code === 'auth/invalid-login-credentials') {
      root.querySelector('[data-field="email"]').classList.add('si-invalid');
      return setFieldError('password', "That email and password don't match a staff account. Check both, or reset your password.");
    }
    if (code === 'auth/popup-closed-by-user' || code === 'auth/cancelled-popup-request') return;
    if (code === 'auth/popup-blocked') return setFieldError('password', 'Your browser blocked the Google window. Allow pop-ups for this site and try again.');
    console.error(`${toolName} sign-in failed:`, err);
    setFieldError('password', 'Sign-in failed. Try again.');
  }

  async function submit(e) {
    e.preventDefault();
    if (busy) return;
    clearErrors();
    const email = emailInput.value.trim();
    const password = passInput.value;
    if (!email) { setFieldError('email', 'Enter your email.'); emailInput.focus(); return; }
    if (!EMAIL_RE.test(email)) { setFieldError('email', "That isn't a valid email address."); emailInput.focus(); return; }
    if (!password) { setFieldError('password', 'Enter your password.'); passInput.focus(); return; }
    if (!navigator.onLine) { showOffline(); return; }
    setBusy(true, 'Signing in…');
    try {
      await signInWithEmailAndPassword(auth, email, password);
      passInput.value = '';
    } catch (err) {
      handleAuthError(err);
    } finally {
      setBusy(false);
    }
  }

  async function google() {
    if (busy) return;
    clearErrors();
    if (!navigator.onLine) { showOffline(); return; }
    setBusy(true);
    try {
      await signInWithPopup(auth, new GoogleAuthProvider());
    } catch (err) {
      handleAuthError(err);
    } finally {
      setBusy(false);
    }
  }

  async function sendReset(e) {
    e.preventDefault();
    if (busy) return;
    clearErrors();
    const email = resetInput.value.trim();
    if (!EMAIL_RE.test(email)) { setFieldError('resetEmail', 'Enter the email you sign in with.'); resetInput.focus(); return; }
    if (!navigator.onLine) { showOffline(); return; }
    setBusy(true);
    try {
      // The link in the email brings them back to the tool they were signing in to.
      await sendPasswordResetEmail(auth, email, { url: location.origin + location.pathname });
      emailInput.value = email;
      showMessage({
        tone: 'green',
        title: 'Check your email',
        text: `We sent a reset link to ${email}. It expires in 1 hour.`,
      });
    } catch (err) {
      const code = err && err.code;
      if (code === 'auth/network-request-failed') showOffline();
      else if (code === 'auth/too-many-requests') setFieldError('resetEmail', 'Too many reset requests. Wait a few minutes and try again.');
      else if (code === 'auth/invalid-email') setFieldError('resetEmail', "That isn't a valid email address.");
      else {
        console.error('Password reset failed:', err);
        setFieldError('resetEmail', "Couldn't send the reset link. Try again.");
      }
    } finally {
      setBusy(false);
    }
  }

  function updateCaps(e) {
    if (typeof e.getModifierState === 'function') capsEl.hidden = !e.getModifierState('CapsLock');
  }

  // ─── Shared tablet: crew picker + PIN pad, and pairing ───────────────────
  let crew = [];
  let picked = null;
  let pin = '';
  let pinBusy = false;
  let onPaired = null;
  const UNPAIRED_NOTICE = 'This tablet was unpaired. Sign in with email, or have an admin pair it again.';

  function pinMsg(text, tone = 'error') {
    const el = $('.si-pin-msg');
    el.textContent = text || '';
    el.dataset.tone = tone;
  }
  function paintDots() {
    root.querySelectorAll('.si-pin-dots span').forEach((d, i) => d.classList.toggle('on', i < pin.length));
  }
  function pickCrew(uid) {
    picked = crew.find(c => c.uid === uid) || null;
    pin = '';
    paintDots();
    pinMsg('');
    root.querySelectorAll('.si-crew-tile').forEach(t => {
      const on = t.dataset.uid === uid;
      t.classList.toggle('on', on);
      t.setAttribute('aria-selected', String(on));
    });
    $('.si-pin-who').textContent = picked ? `${picked.name.split(' ')[0]}, enter your PIN` : 'Pick your name';
    pinFrame.classList.toggle('si-pin-ready', !!picked);
  }

  async function showPinPane() {
    const dev = pairedDevice();
    if (!dev) { show(); return; }
    root.querySelector('.si-frame').hidden = true;
    pinFrame.hidden = false;
    root.style.display = '';
    $('.si-device-name').textContent = dev.name || 'Shared tablet';
    $('.si-device-sub').textContent = pairedLine(dev);
    crew = [];
    pickCrew(null);
    const grid = $('.si-crew-grid');
    if (!navigator.onLine) {
      grid.innerHTML = '<div class="si-crew-empty"><b>You\'re offline</b>Signing in needs a connection. <button type="button" class="si-link si-link-mint" data-act="crew">Try again</button></div>';
      return;
    }
    grid.innerHTML = '<div class="si-crew-empty">Loading the crew…</div>';
    try {
      const res = await httpsCallable(functions, 'listSharedCrew')({ deviceId: dev.deviceId, secret: dev.secret });
      const { device, crew: list } = res.data || {};
      crew = list || [];
      if (device) {
        const merged = { ...dev, name: device.name || dev.name, pairedBy: device.pairedBy, pairedAt: device.pairedAt };
        $('.si-device-name').textContent = merged.name;
        $('.si-device-sub').textContent = pairedLine(merged);
        try { localStorage.setItem(DEVICE_KEY, JSON.stringify(merged)); } catch { /* ignore */ }
      }
      grid.innerHTML = crew.length
        ? crew.map(c => `<button type="button" class="si-crew-tile" role="option" aria-selected="false" data-uid="${escapeHTML(c.uid)}">
            <span class="si-crew-ini">${escapeHTML(c.initials)}</span><span class="si-crew-name">${escapeHTML(c.name)}</span></button>`).join('')
        : '<div class="si-crew-empty"><b>No one has a PIN yet</b>Each person sets a 4-digit PIN in the Techs app (Me) or Settings › My profile, then shows up here.</div>';
      if (crew.length === 1) pickCrew(crew[0].uid);
    } catch (err) {
      if (/not paired/i.test(err?.message || '')) { forgetDevice(); show({ notice: UNPAIRED_NOTICE }); return; }
      grid.innerHTML = `<div class="si-crew-empty"><b>Couldn't load the crew</b>${navigator.onLine ? 'Something went wrong. ' : 'The tablet lost its connection. '}<button type="button" class="si-link si-link-mint" data-act="crew">Try again</button></div>`;
    }
  }

  function shakeDots() {
    const dots = pinFrame.querySelector('.si-pin-dots');
    dots.classList.remove('si-shake');
    void dots.offsetWidth;
    dots.classList.add('si-shake');
  }

  async function submitPin() {
    const dev = pairedDevice();
    if (!dev || !picked || pin.length !== 4 || pinBusy) return;
    pinBusy = true;
    pinMsg('Checking…', 'info');
    try {
      const res = await httpsCallable(functions, 'pinSignIn')({ deviceId: dev.deviceId, secret: dev.secret, uid: picked.uid, pin });
      await signInWithCustomToken(auth, res.data.token);
      pinMsg('');
    } catch (err) {
      const msg = err?.message || '';
      if (/not paired/i.test(msg)) { forgetDevice(); show({ notice: UNPAIRED_NOTICE }); return; }
      if (!navigator.onLine || err?.code === 'functions/unavailable' || err?.code === 'auth/network-request-failed') pinMsg("You're offline. Signing in needs a connection.");
      else pinMsg(msg && !/^internal$/i.test(msg) ? msg : "Couldn't sign in. Try again.");
      shakeDots();
    } finally {
      pin = '';
      paintDots();
      pinBusy = false;
    }
  }

  function pressKey(k) {
    if (pinBusy) return;
    if (!picked) { pinMsg('Pick your name first.', 'info'); return; }
    if (k === 'back') pin = pin.slice(0, -1);
    else if (pin.length < 4) { pin += k; pinMsg(''); }
    paintDots();
    if (pin.length === 4) submitPin();
  }

  if (pinFrame) {
    pinFrame.addEventListener('click', (e) => {
      const tile = e.target.closest('.si-crew-tile');
      if (tile) { pickCrew(tile.dataset.uid); return; }
      const key = e.target.closest('.si-key');
      if (key) pressKey(key.dataset.key);
    });
    document.addEventListener('keydown', (e) => {
      if (pinFrame.hidden || root.style.display === 'none') return;
      if (/^\d$/.test(e.key)) { pressKey(e.key); e.preventDefault(); }
      else if (e.key === 'Backspace') { pressKey('back'); e.preventDefault(); }
    });
  }

  function showPairPane(user, done) {
    onPaired = done;
    root.style.display = '';
    showPane('pair');
    $('.si-pair-who').innerHTML = `Signed in as <b>${escapeHTML(user.displayName || user.email || 'an admin')}</b>. Give this tablet a name the crew will recognise.`;
    setTimeout(() => $('#siDeviceName').focus(), 0);
  }

  async function doPair(e) {
    e.preventDefault();
    if (busy) return;
    clearErrors();
    const name = $('#siDeviceName').value.trim();
    if (!name) { setFieldError('deviceName', 'Give the tablet a name, e.g. "Launch 2 tablet".'); return; }
    if (!navigator.onLine) { showOffline(); return; }
    busy = true;
    root.querySelectorAll('.si-btn').forEach(b => { b.disabled = true; });
    try {
      const res = await httpsCallable(functions, 'pairSharedDevice')({ name, tool: toolName });
      const { deviceId, secret, pairedBy } = res.data;
      localStorage.setItem(DEVICE_KEY, JSON.stringify({ deviceId, secret, name, pairedBy, pairedAt: Date.now() }));
      setPairPending(false);
      if (onPaired) onPaired();
    } catch (err) {
      setFieldError('deviceName', err?.message && !/^internal$/i.test(err.message) ? err.message : "Couldn't pair the tablet. Try again.");
    } finally {
      busy = false;
      root.querySelectorAll('.si-btn').forEach(b => { b.disabled = false; });
    }
  }

  if (sharedTablet) $('[data-pane="pair"]').addEventListener('submit', doPair);

  $('[data-pane="form"]').addEventListener('submit', submit);
  $('[data-pane="reset"]').addEventListener('submit', sendReset);
  root.addEventListener('click', (e) => {
    const act = e.target.closest('[data-act]')?.dataset.act;
    if (act === 'google') google();
    else if (act === 'forgot') openReset();
    else if (act === 'back') { clearErrors(); showPane('form'); }
    else if (act === 'msgAction' && msgAction) msgAction();
    else if (act === 'pair') { setPairPending(true); show({ notice: 'Sign in as an admin to pair this tablet.' }); }
    else if (act === 'cancelPair') { setPairPending(false); signOut(auth).catch(() => {}); }
    else if (act === 'crew') showPinPane();
    else if (act === 'email') show();
    else if (act === 'show') {
      const showing = passInput.type === 'text';
      passInput.type = showing ? 'password' : 'text';
      e.target.textContent = showing ? 'Show' : 'Hide';
      e.target.setAttribute('aria-pressed', String(!showing));
    }
  });
  passInput.addEventListener('keydown', updateCaps);
  passInput.addEventListener('keyup', updateCaps);
  passInput.addEventListener('blur', () => { capsEl.hidden = true; });
  emailInput.addEventListener('input', () => setFieldError('email', ''));
  passInput.addEventListener('input', () => {
    setFieldError('password', '');
    root.querySelector('[data-field="email"]').classList.remove('si-invalid');
  });
  resetInput.addEventListener('input', () => setFieldError('resetEmail', ''));
  window.addEventListener('online', () => {
    if (!$('[data-pane="message"]').hidden && $('.si-msg').classList.contains('si-panel-neutral')) showPane('form');
  });

  loadBrand();

  /** The sign-in form; `notice` is an optional neutral line above the fields. */
  function show({ notice = '' } = {}) {
    const n = $('.si-notice');
    n.textContent = notice;
    n.hidden = !notice;
    clearErrors();
    showPane('form');
    root.style.display = '';
    if (!emailInput.value) setTimeout(() => emailInput.focus(), 0);
  }

  return {
    root,
    show,
    /** Shared tablet: the crew picker + PIN pad (the email form when not paired). */
    showPin: showPinPane,
    /** Shared tablet: an admin signed in to pair — name the tablet. */
    showPair: showPairPane,
    hide() { root.style.display = 'none'; },
    /**
     * Signed in, but not allowed in: the role this tool needs, who can grant it, and a
     * way out. role === null means the account has no role at all yet.
     */
    async showAccess({ user, role, requiredRole = 'admin', reason = '', parent = null }) {
      root.style.display = '';
      showPane('access');
      const pane = $('[data-pane="access"]');
      const who = escapeHTML(user.displayName || user.email || 'this account');
      const b = cachedBrand() || {};
      const head = `<div class="si-access-brand"><img data-brand="logo" src="${escapeHTML(brandLogo(b.logoUrl) || DEFAULT_LOGO)}" alt="" width="28" height="28"><span data-brand="name">${escapeHTML(b.name || '')}</span></div>`;
      const title = role
        ? `${escapeHTML(toolName)} is for ${escapeHTML(requiredRole)}s`
        : 'Your account isn’t set up yet';
      const body = role
        ? `You’re signed in as <b>${who}</b> (${escapeHTML(roleLabel(role))}). ${escapeHTML(reason)} This tool needs ${escapeHTML(roleLabel(requiredRole))} access.`
        : `You’re signed in as <b>${who}</b>, but no role has been assigned to this account. Ask an admin to give you access.`;
      pane.innerHTML = `${head}
        <h1 class="si-title si-access-title">${title}</h1>
        <p class="si-text">${body}</p>
        <div class="si-admins" hidden>
          <div class="si-admins-label">Admins who can give you access</div>
          <div class="si-admins-list"></div>
        </div>
        <div class="si-access-actions">
          <button type="button" class="si-btn si-btn-quiet" data-access="switch">Switch account</button>
          ${parent
            ? `<a class="si-btn si-btn-navy" href="${escapeHTML(parent.href)}">Open ${escapeHTML(parent.label)}</a>`
            : '<button type="button" class="si-btn si-btn-navy" data-access="retry">Check again</button>'}
        </div>`;
      pane.querySelector('[data-access="switch"]').addEventListener('click', () => {
        signOut(auth).catch(() => {});
      });
      pane.querySelector('[data-access="retry"]')?.addEventListener('click', () => location.reload());

      // /users is readable by any account with a role; one with no role can't list admins.
      if (!role) return;
      try {
        const snap = await get(ref(database, 'users'));
        const admins = [];
        snap.forEach(child => {
          const u = child.val() || {};
          if (child.key !== user.uid && isAdminRole(String(u.role || '')) && u.email) {
            admins.push({ name: u.displayName || u.email, email: u.email });
          }
        });
        if (!admins.length) return;
        admins.sort((a, b2) => a.name.localeCompare(b2.name));
        const subject = encodeURIComponent(`Access to ${toolName}`);
        const mailBody = encodeURIComponent(`Hi, could you give my account (${user.email || ''}) access to ${toolName}? Thanks.`);
        pane.querySelector('.si-admins-list').innerHTML = admins.slice(0, 6).map(a => `
          <div class="si-admin">
            <span class="si-avatar">${escapeHTML(initialsOf(a.name))}</span>
            <span class="si-admin-name">${escapeHTML(a.name)}</span>
            <a class="si-link" href="mailto:${escapeHTML(a.email)}?subject=${subject}&body=${mailBody}">Request</a>
          </div>`).join('');
        pane.querySelector('.si-admins').hidden = false;
      } catch { /* list is a nice-to-have */ }
    },
  };
}

/**
 * Standalone staff tool gate. onReady(user, role) runs once, the first time a user who
 * may use the tool is signed in. requiredRole: undefined = any staff role; 'admin' = admin.
 */
export function gateTool({ toolName, requiredRole, reason = '', parent = null, onReady, sharedTablet = false }) {
  const screen = createSignIn({ toolName, sharedTablet });
  let started = false;
  let startedUid = null;
  let nextNotice = '';
  if (sharedTablet) watchIdle();
  onAuthStateChanged(auth, async (user) => {
    if (!user) {
      if (sharedTablet && pairedDevice() && !pairPending() && !nextNotice) screen.showPin();
      else screen.show({ notice: nextNotice || (sharedTablet && pairPending() ? 'Sign in as an admin to pair this tablet.' : '') });
      nextNotice = '';
      return;
    }
    const role = await getUserRole(user.uid);
    if (sharedTablet && pairPending()) {
      if (role && isAdminRole(String(role))) {
        // Paired: sign the admin out so the tablet opens on the crew list.
        screen.showPair(user, () => { signOut(auth).catch(() => {}); });
      } else {
        setPairPending(false);
        nextNotice = 'Only an admin can pair a tablet. Have an admin sign in and tap Pair this device.';
        signOut(auth).catch(() => {});
      }
      return;
    }
    // Someone else signed in after the tablet locked: start the tool fresh as them.
    if (started && startedUid && startedUid !== user.uid) { location.reload(); return; }
    const allowed = role && (!requiredRole || (requiredRole === 'admin' && isAdminRole(String(role))));
    if (!allowed) {
      screen.showAccess({ user, role: role || null, requiredRole: requiredRole || 'staff', reason, parent });
      return;
    }
    screen.hide();
    if (!started) { started = true; startedUid = user.uid; onReady(user, role); }
  });
  return screen;
}

/** A paired tablet signs its user out after 20 minutes without a touch. */
function watchIdle() {
  let last = Date.now();
  const touch = () => { last = Date.now(); };
  ['pointerdown', 'keydown'].forEach(t => document.addEventListener(t, touch, { capture: true, passive: true }));
  setInterval(() => {
    if (!pairedDevice() || !auth.currentUser) { touch(); return; }
    if (Date.now() - last >= IDLE_LOCK_MS) { touch(); signOut(auth).catch(() => {}); }
  }, 30 * 1000);
}
